import uniqueId from 'lodash/uniqueId';
import React from 'react';
import type { PrettifiedBuffer, PrettifiedBufferActionGroup } from './types';
import prettifiedAtoms from './components';
import { NestedComponentContainer } from '../../styles';

export default function createPrettifiedBuffer(): PrettifiedBuffer {
  const prettifiedLinesPerActionPublicId: Record<string, PrettifiedBufferActionGroup> = {};

  return {
    append: entry => {
      const arr = Array.isArray(entry) ? entry : [entry];
      arr.forEach(entryToAppend => {
        const {
          actionPublicId,
          line,
          id = uniqueId(),
          isNested = false,
        } = entryToAppend;

        if (!Object.hasOwn(prettifiedLinesPerActionPublicId, actionPublicId)) {
          prettifiedLinesPerActionPublicId[actionPublicId] = {
            items: [],
            isNested,
          };
        }

        prettifiedLinesPerActionPublicId[actionPublicId].items.push({
          Component: line,
          id,
        });
      });
    },
    package: () => (
      /* a little negative margin is added to account for the margin-top the of the first heading1 */
      <div style={{ height: '100%', width: '100%', marginTop: 'calc(-1 * var(--cl-space-2))' }}>
        {(
          Object
            .entries(prettifiedLinesPerActionPublicId)
            .flatMap(([actionPublicId, group], idx, array) => {
              // render every line for the action
              const linesForAction = group.items;
              const Components = linesForAction.map(({ Component, id }) => <Component key={id} />);
              const EmptyLine = prettifiedAtoms.emptyLine();

              const isLastAction = idx + 1 === array.length;
              const isNextActionNested = array[idx + 1]?.[1]?.isNested === true;

              /**
               * separate every action section by an extra empty line.
               *  - skips adding it on the last action to avoid creating dead space at the bottom.
               *  - skips adding it for actions preceded by a nested section
               */
              const content = isLastAction || isNextActionNested
                ? Components
                : [
                  ...Components,
                  <EmptyLine key={actionPublicId} />,
                ];

              if (group.isNested) {
                return [
                  <NestedComponentContainer key={actionPublicId}>
                    {content}
                  </NestedComponentContainer>,
                ];
              }

              return content;
            })
        )}
      </div>
    ),
  };
}
